import {
  Checkbox, Col,
  InputNumber, Row,
  TreeSelectProps,
} from 'antd';

import { getFormattedPercent } from 'utils';

import { AggregatedProductIdentity, AggregatedProductIdentityUpdate, ProductIdentityValue } from 'codegen';
import {
  TColumn,
  TEditableColumn,
  TFieldProps,
  TRequestCallback,
} from 'types';

import {
  AddNewIdentityButton,
  DisableIdentityAction,
  IdentityValue,
  ThresholdCheckbox,
  ThresholdInput,
  TypeSelect,
} from '../component';

export const columns = (
  retailers: TreeSelectProps['treeData'],
  refresh: () => void,
  patchIdentities: TRequestCallback<AggregatedProductIdentityUpdate>,
): TEditableColumn<AggregatedProductIdentity & { id: number }>[] => [{
  width: 80,
  title: 'Type',
  dataIndex: 'type',
  filters: Object.values(AggregatedProductIdentity.type).map((type) => ({ text: type, value: type })),
  onFilter: (value, record) => record.type.startsWith(String(value)),
  field: {
    type: TFieldProps.type.Custom,
    rules: [{
      message: 'Required',
      required: true,
    }],
    renderCustomField: TypeSelect,
  },
}, {
  width: 250,
  title: 'Values',
  dataIndex: 'values',
  field: ({ isFieldAdding }) => {
    if (isFieldAdding) {
      return {
        type: TFieldProps.type.Text,
        placeholder: 'Fill the value',
        rules: [{
          message: 'Required',
          required: true,
        }],
      };
    }

    return null;
  },
  render: (values: ProductIdentityValue[], record) => {
    if (values) {
      return (
        <>
          {values.map((value) => (
            <Row key={value.id} wrap={false} align='middle' justify='space-between'>
              <Col flex={1}>
                <IdentityValue
                  record={record}
                  identity={value}
                  retailers={retailers}
                  refresh={refresh}
                />
              </Col>

              <Col>
                <DisableIdentityAction
                  record={record}
                  identity={value}
                  patchIdentities={patchIdentities}
                />
              </Col>
            </Row>
          ))}

          <AddNewIdentityButton
            record={record}
            refresh={refresh}
          />
        </>
      );
    }

    return null;
  },
}, {
  type: TColumn.type.Tags,
  width: 350,
  title: 'Retailers',
  dataIndex: 'retailers',
  field: {
    type: TFieldProps.type.TreeSelect,
    treeData: retailers,
    placeholder: 'Select Retailer(s)',
    rules: [{
      message: 'Required',
      required: true,
    }],
  },
}, {
  width: 80,
  dataIndex: 'partialMatchThreshold',
  titleInfo: {
    title: 'Partial Match Threshold',
    info: 'Reducing predefined Partial Match Threshold can result in an increased number of matches that require manual correction',
  },
  render: (value, { hasAutoCalculatedThresholds }) => (
    <>
      <InputNumber
        disabled
        value={hasAutoCalculatedThresholds ? 'AUTO' : getFormattedPercent((value || 0) * 100)}
      />

      <Checkbox
        disabled
        checked={hasAutoCalculatedThresholds}
        className='mt-7'
      >
        Auto
      </Checkbox>
    </>
  ),
  field: () => ({
    type: TFieldProps.type.Custom,
    renderCustomField: () => (
      <>
        <ThresholdInput
          name='partialMatchThreshold'
          placeholder='Fill partial match value'
          dependencies={['fullMatchThreshold']}
          rules={[
            {
              message: 'Required',
              required: true,
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('fullMatchThreshold') > value) {
                  return Promise.resolve();
                }

                return Promise.reject(new Error('Should be less than Full Match'));
              },
            }),
          ]}
        />

        <ThresholdCheckbox />
      </>
    ),
  }),
}, {
  width: 80,
  dataIndex: 'fullMatchThreshold',
  titleInfo: {
    title: 'Full Match Threshold',
    info: 'Reducing predefined Full Match Threshold can result in increased amount of matches that are falsely granted  with miles',
  },
  render: (value, { hasAutoCalculatedThresholds }) => (
    <>
      <InputNumber
        disabled
        value={hasAutoCalculatedThresholds ? 'AUTO' : getFormattedPercent((value || 0) * 100)}
      />

      <Checkbox
        disabled
        checked={hasAutoCalculatedThresholds}
        className='mt-7'
      >
        Auto
      </Checkbox>
    </>
  ),
  field: () => ({
    type: TFieldProps.type.Custom,
    renderCustomField: () => (
      <>
        <ThresholdInput
          name='fullMatchThreshold'
          placeholder='Fill full match value'
          dependencies={['partialMatchThreshold']}
          rules={[
            {
              message: 'Required',
              required: true,
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('partialMatchThreshold') < value) {
                  return Promise.resolve();
                }

                return Promise.reject(new Error('Should be more than Partial Match'));
              },
            }),
          ]}
        />

        <ThresholdCheckbox />
      </>
    ),
  }),
}];
